<template>
  <div class="telemedicina-page">
    <!-- Hero Section -->
    <section class="hero-section hero-animate" :style="{ backgroundImage: `url(${heroImage})` }">
      <div class="container">
        <div class="row">
          <div class="col-lg-6">
            <h1 class="hero-title">
              <span class="title-primary">Atención médica</span> desde la comodidad de <span class="title-primary">tu hogar</span>
            </h1>
            <p class="hero-description">
              Consulta con médicos certificados en minutos, desde tu celular o computadora. Sin salir de casa, sin perder trabajo, sin esperas. Atención médica de calidad 24/7, cuando realmente la necesitas. Miles de familias ya están usando telemedicina. ¿Qué estás esperando?
            </p>
            <div class="hero-buttons">
              <router-link to="/contact" class="btn btn-secondary-hero btn-lg">
                Contáctanos
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="benefits-section">
      <div class="container">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="section-title">Beneficios de la Telemedicina</h2>
          <p class="section-description">
            La medicina del futuro, disponible hoy. Consultas ilimitadas sin costo adicional, médicos certificados y resultados inmediatos. Tu salud, en tus manos, cuando la necesites.
          </p>
        </div>

        <div class="row align-items-center">
          <!-- Columna Izquierda: Cards en fila -->
          <div class="col-lg-6">
            <div class="benefits-cards-column">
              <div class="benefit-item animate-on-scroll fade-in-up">
                <div class="benefit-icon">
                  <img :src="benefitIcons[0]" alt="Desde tu hogar" />
                </div>
                <div class="benefit-card">
                  <h4 class="benefit-title">Desde tu hogar</h4>
                  <p class="benefit-text">
                    ¿Tu hijo tiene fiebre a las 10 PM? ¿No puedes faltar al trabajo? Consulta con médicos certificados desde donde estés, cuando lo necesites. Sin perder tiempo en tráfico, sin esperas en salas de espera. Tu salud, sin complicaciones.
                  </p>
                </div>
              </div>

              <div class="benefit-item animate-on-scroll fade-in-up" style="animation-delay: 0.1s">
                <div class="benefit-icon">
                  <img :src="benefitIcons[1]" alt="Disponible 24/7" />
                </div>
                <div class="benefit-card">
                  <h4 class="benefit-title">Disponible 24/7</h4>
                  <p class="benefit-text">
                    Emergencias médicas no esperan horarios de oficina. Accede a consultas médicas de 09:00 AM a 10:00 PM, todos los días incluidos domingos y días festivos. Cuando tu salud lo necesite, nosotros estamos disponibles. Sin restricciones, sin límites.
                  </p>
                </div>
              </div>

              <div class="benefit-item animate-on-scroll fade-in-up" style="animation-delay: 0.2s">
                <div class="benefit-icon">
                  <img :src="benefitIcons[2]" alt="Seguro y confidencial" />
                </div>
                <div class="benefit-card">
                  <h4 class="benefit-title">Seguro y confidencial</h4>
                  <p class="benefit-text">
                    Tu privacidad es sagrada. Somos HIPAA compliant: cumplimos con los estándares federales de protección de información de salud que exige la ley en Estados Unidos. Tus consultas y datos médicos están protegidos con los mismos protocolos de privacidad y seguridad que rigen a hospitales y clínicas. Tu información médica, solo tuya.
                  </p>
                </div>
              </div>

              <div class="benefit-item animate-on-scroll fade-in-up" style="animation-delay: 0.3s">
                <div class="benefit-icon">
                  <img :src="benefitIcons[3]" alt="Ahorro económico" />
                </div>
                <div class="benefit-card">
                  <h4 class="benefit-title">Ahorro económico</h4>
                  <p class="benefit-text">
                    Sin gastar en gasolina, sin perder horas de trabajo, sin pagar estacionamiento. Las consultas de telemedicina están 100% cubiertas en tu plan. Ahorra tiempo y dinero, mientras cuidas tu salud y la de tu familia.
                  </p>
                </div>
              </div>

              <div class="benefit-item animate-on-scroll fade-in-up" style="animation-delay: 0.4s">
                <div class="benefit-icon">
                  <img :src="benefitIcons[4]" alt="Médicos certificados" />
                </div>
                <div class="benefit-card">
                  <h4 class="benefit-title">Médicos certificados</h4>
                  <p class="benefit-text">
                    No son médicos de segunda. Son profesionales licenciados, con años de experiencia, especializados en atención virtual. La misma calidad que encontrarías en una clínica física, pero con la comodidad de tu hogar.
                  </p>
                </div>
              </div>

              <div class="benefit-item animate-on-scroll fade-in-up" style="animation-delay: 0.5s">
                <div class="benefit-icon">
                  <img :src="benefitIcons[5]" alt="Recetas digitales" />
                </div>
                <div class="benefit-card">
                  <h4 class="benefit-title">Recetas digitales</h4>
                  <p class="benefit-text">
                    Tu receta llega a tu celular en segundos. Sin papeles, sin esperas. Úsala inmediatamente en cualquier farmacia. Con nuestros descuentos de hasta 80% en medicamentos, ahorras desde el primer día.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Columna Derecha: Imagen -->
          <div class="col-lg-6">
            <div class="benefits-image-wrapper animate-on-scroll fade-in-right">
              <img :src="benefitsImage" alt="Beneficios de la Telemedicina" class="benefits-image" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Content -->
    <section class="how-it-works-content-section">
      <div class="container">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="section-title">¿Cómo funciona?</h2>
          <p class="section-description">
            Proceso simple y rápido para tu consulta médica virtual
          </p>
        </div>
      </div>
    </section>

    <!-- How It Works Images - Oculto en mobile -->
    <section class="how-it-works-images-section d-none d-md-block" :style="{ backgroundImage: `url(${howItWorksImageTop}), url(${howItWorksImageBottom})` }">
    </section>

    <!-- How It Works Cards -->
    <section class="how-it-works-cards-section">
      <div class="container">
        <div class="row">
          <div class="col-lg-3 col-md-6 mb-4">
            <div class="step-card step-card-white animate-on-scroll fade-in-up">
              <div class="step-icon">
                <img :src="stepIcons[0]" alt="Regístrate" />
              </div>
              <h4 class="step-title">Regístrate</h4>
              <p class="step-text">
                En menos de 5 minutos, únete a miles de familias que ya están protegiendo su salud. Selecciona el plan que mejor se adapte a ti y comienza a ahorrar desde hoy.
              </p>
            </div>
          </div>

          <div class="col-lg-3 col-md-6 mb-4">
            <div class="step-card step-card-white animate-on-scroll fade-in-up" style="animation-delay: 0.1s">
              <div class="step-icon">
                <img :src="stepIcons[1]" alt="Agenda tu cita" />
              </div>
              <h4 class="step-title">Agenda tu cita</h4>
              <p class="step-text">
                O consulta de inmediato. Elige el momento que funcione para ti: ahora mismo, esta tarde, o mañana. Tu agenda, tu control. Sin esperas de semanas como en las clínicas tradicionales.
              </p>
            </div>
          </div>

          <div class="col-lg-3 col-md-6 mb-4">
            <div class="step-card step-card-primary animate-on-scroll fade-in-up" style="animation-delay: 0.2s">
              <div class="step-icon">
                <img :src="stepIcons[2]" alt="Conecta con el médico" />
              </div>
              <h4 class="step-title">Conecta con el médico</h4>
              <p class="step-text">
                Un clic y estás conectado. Desde tu celular, tablet o computadora. El médico te espera, listo para ayudarte. Sin complicaciones técnicas, sin instalaciones complicadas. Simple y rápido.
              </p>
            </div>
          </div>

          <div class="col-lg-3 col-md-6 mb-4">
            <div class="step-card step-card-white animate-on-scroll fade-in-up" style="animation-delay: 0.3s">
              <div class="step-icon">
                <img :src="stepIcons[3]" alt="Recibe atención" />
              </div>
              <h4 class="step-title">Recibe atención</h4>
              <p class="step-text">
                Diagnóstico profesional, receta digital en minutos, y la tranquilidad de saber que estás bien cuidado. Todo sin salir de casa. Tu salud resuelta, tu tiempo respetado.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="row">
          <div class="col-lg-8 mx-auto text-center">
            <div class="cta-content animate-on-scroll fade-in-up">
              <h2 class="cta-title">¿Listo para comenzar?</h2>
              <p class="cta-description">
                Miles de familias ya están ahorrando tiempo y dinero con telemedicina. Consultas ilimitadas sin costo adicional, médicos certificados 24/7, y recetas digitales instantáneas. Tu salud, cuando la necesites, donde estés. ¿Qué estás esperando?
              </p>
              <div class="cta-buttons">
                <router-link to="/plans" class="btn btn-primary-cta btn-lg">
                  Activa tu Beneficio ahora
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "Telemedicina",
  data() {
    return {
      heroImage: require('@/assets/lp/11.png'),
      benefitsImage: require('@/assets/lp/12.png'),
      howItWorksImageTop: require('@/assets/lp/14.png'),
      howItWorksImageBottom: require('@/assets/lp/13.png'),
      benefitIcons: [
        require('@/assets/lp/Recurso 16@300xe.webp'),
        require('@/assets/lp/Recurso 17@300xe.webp'),
        require('@/assets/lp/Recurso 18@300x.webp'),
        require('@/assets/lp/Recurso 19@300x.webp'),
        require('@/assets/lp/Recurso 20@300x.webp'),
        require('@/assets/lp/Recurso 21@300xe.webp'),
      ],
      stepIcons: [
        require('@/assets/lp/Recurso 22@300xt.webp'),
        require('@/assets/lp/Recurso 23@300xt.webp'),
        require('@/assets/lp/Recurso 24@300xt.webp'),
        require('@/assets/lp/Recurso 25@300xt.webp'),
      ],
    };
  },
  mounted() {
    this.setupScrollAnimations();
  },
  methods: {
    setupScrollAnimations() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          } else {
            entry.target.classList.remove('is-visible');
          }
        });
      }, observerOptions);

      this.$nextTick(() => {
        const elements = this.$el.querySelectorAll('.animate-on-scroll');
        elements.forEach(el => observer.observe(el));
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.telemedicina-page {
  .hero-section {
    padding: 100px 0;
    color: white;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    
    .hero-title {
      font-size: 4.5rem;
      font-weight: 700;
      margin-bottom: 2rem;
      color: white;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      line-height: 1.2;
      
      .title-primary {
        color: $landing-secondary-color;
      }
    }
    
    .hero-description {
      font-size: 1.3rem;
      color: white;
      font-weight: 500;
      margin-bottom: 2rem;
      max-width: 800px;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    }
    
    .btn-secondary-hero {
      background: $landing-secondary-color;
      color: white;
      border: none;
      border-radius: 50px;
      padding: 15px 40px;
      font-size: 1.5rem;
      font-weight: 700;
      transition: all 0.3s ease;
      
      &:hover {
        background: darken($landing-secondary-color, 10%);
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(34, 207, 224, 0.4);
        color: white;
        text-decoration: none;
      }
    }
  }

  .benefits-section {
    padding: 100px 0;
    background: white;
    
    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
      line-height: 1.2;
    }
    
    .section-description {
      font-size: 1.8rem;
      color: $landing-primary-color;
      max-width: 800px;
      margin: 0 auto;
      line-height: 1.7;
      font-weight: 500;
    }
    
    .benefits-cards-column {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }
    
    .benefit-item {
      display: flex;
      align-items: center;
      gap: 1.5rem;
      
      .benefit-icon {
        width: 120px;
        height: 120px;
        min-width: 120px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
      
      &:hover {
        .benefit-icon {
          transform: scale(1.1);
        }
        
        .benefit-card {
          transform: translateX(8px);
          box-shadow: 0 15px 40px $landing-primary-rgba-25;
          background: linear-gradient(135deg, $landing-bg-white 0%, rgba(232, 244, 248, 1) 100%);
          border-color: $landing-primary-rgba-30;
        }
      }
    }
    
    .benefit-card {
      background: linear-gradient(135deg, $landing-bg-white 0%, $landing-bg-light 100%);
      padding: 2rem;
      border-radius: 15px;
      box-shadow: 0 5px 20px $landing-primary-rgba-08;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      border: 1px solid $landing-primary-rgba-15;
      flex: 1;
      
      .benefit-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: $landing-text-dark;
        margin-bottom: 0.75rem;
      }
      
      .benefit-text {
        color: $landing-text-dark;
        line-height: 1.7;
        margin: 0;
      }
    }
    
    .benefits-image-wrapper {
      text-align: center;
      background: transparent;
      padding: 0;
      
      .benefits-image {
        width: 120%;
        height: auto;
        max-width: none;
        border-radius: 0;
        box-shadow: none;
        margin-left: -10%;
      }
    }
  }

  .how-it-works-images-section {
    min-height: 400px;
    background-position: top -50px center, bottom center;
    background-repeat: no-repeat, no-repeat;
    background-size: 60% auto, 100% auto;
    margin-top: -50px;
    padding-top: 50px;
    overflow: visible;
  }

  .how-it-works-content-section {
    padding: 60px 0;
    background: white;
    
    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
      line-height: 1.2;
    }
    
    .section-description {
      font-size: 1.8rem;
      color: $landing-primary-color;
      max-width: 800px;
      margin: 0 auto;
      line-height: 1.7;
      font-weight: 500;
    }
  }

  .how-it-works-cards-section {
    padding: 60px 0 100px;
    background: white;
    
    .step-card {
      padding: 2.5rem;
      border-radius: 15px;
      text-align: center;
      position: relative;
      transition: all 0.3s ease;
      height: 100%;
      
      &.step-card-white {
        background: white;
        border: 2px solid #e5eaee;
        
        .step-title {
          color: $landing-primary-color;
        }
        
        .step-text {
          color: $landing-primary-color;
        }
        
        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px $landing-primary-rgba-10;
          border-color: $landing-primary-color;
        }
      }
      
      &.step-card-primary {
        background: $landing-primary-color;
        border: 2px solid #e5eaee;
        
        .step-title {
          color: white;
        }
        
        .step-text {
          color: white;
        }
        
        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px $landing-primary-rgba-40;
          border-color: $landing-secondary-color;
        }
      }
      
      .step-icon {
        width: 80px;
        height: 80px;
        margin: 0 auto 1.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
      
      .step-title {
        font-size: 1.8rem;
        font-weight: 700;
        margin-bottom: 1rem;
      }
      
      .step-text {
        font-size: 1.2rem;
        line-height: 1.7;
      }
    }
  }

  .cta-section {
    padding: 100px 0;
    background: linear-gradient(135deg, $landing-primary-color 0%, $landing-secondary-color 100%);
    
    .cta-title {
      font-size: 2.5rem;
      font-weight: 700;
      color: white;
      margin-bottom: 1.5rem;
      text-shadow: 0 4px 15px $landing-primary-rgba-40, 0 2px 8px rgba(0, 0, 0, 0.2);
      line-height: 1.2;
    }
    
    .cta-description {
      font-size: 1.5rem;
      color: white;
      margin-bottom: 2.5rem;
      font-weight: 500;
      line-height: 1.7;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
    
    .btn-primary-cta {
      background: linear-gradient(135deg, $landing-primary-color 0%, $landing-secondary-color 100%);
      color: white;
      border: none;
      border-radius: 50px;
      padding: 20px 50px;
      font-size: 1.4rem;
      font-weight: 700;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 10px 30px $landing-primary-rgba-45;
      
      &:hover {
        background: $landing-primary-gradient-dark;
        transform: translateY(-4px) scale(1.02);
        box-shadow: 0 15px 40px $landing-primary-rgba-55;
        color: white;
        text-decoration: none;
      }
    }
  }

  @media (max-width: 768px) {
    .hero-section {
      padding: 100px 24px;
      min-height: 420px;
      background-size: cover;
      background-position: center;

      .hero-title {
        font-size: 2.5rem;
      }
      
      .btn-secondary-hero {
        font-size: 1.2rem;
      }
    }
    
    .section-title {
      font-size: 2rem !important;
    }
    
    .benefits-section {
      padding: 60px 0;
      
      .benefit-item {
        flex-direction: column;
        text-align: center;
        gap: 1rem;
        
        .benefit-icon {
          margin-bottom: 0.5rem;
        }
        
        .benefit-card {
          padding: 1.5rem;
        }
      }
      
      .benefits-image-wrapper {
        margin-top: 2rem;
      }
    }
  }
}
</style>

